import { Injectable } from '@angular/core';
import { where } from '@angular/fire/firestore';
import { IAgent } from '@shared/interfaces';
import { SharedFirestoreService } from 'libs/shared/src/lib/services/firestore.service';
import { ChannelsService } from './channels.service';
import { OnboardingService } from './onboarding.service';
import { UserService } from './user.service';
import { BehaviorSubject } from 'rxjs';

export interface ISimulationMessage {
  content: string;
  role: 'system' | 'user' | 'assistant';
  timestamp: string;
}

@Injectable({
  providedIn: 'root',
})
export class AgentsService {
  agents: IAgent[] = [];
  messageContent?: string;
  exampleMessages: ISimulationMessage[] = [];
  private agentSubject = new BehaviorSubject<IAgent>({
    uid: '',
    name: '',
    organization: '',
    createdAt: new Date(),
    deletedAt: new Date(),
    configuration: {
      prompt: '',
    },
  });

  agent$ = this.agentSubject.asObservable();

  constructor(
    private firestore: SharedFirestoreService,
    private userService: UserService,
    private channelsService: ChannelsService,
    private onboardingService: OnboardingService,
  ) {}

  async getAgents() {
    this.agents = [];
    const orgId = this.userService.organization()?.uid;
    if (!orgId) return;

    try {
      const agentsQuerySnap = await this.firestore.getDocs(
        'agent',
        where('organization', '==', orgId),
        where('deletedAt', '==', null),
      );

      for (const queryDocSnap of agentsQuerySnap.docs) {
        this.agents.push(queryDocSnap.data() as IAgent);
      }
    } catch (error) {
      console.error('Error getting agents:', error);
      this.agents = [];
    }
  }

  async getAgent(id: string): Promise<IAgent> {
    const agent = await this.firestore.getDoc<IAgent>('agent', id);
    if (!agent) {
      throw new Error('Agent not found');
    }
    this.setAgent(agent);
    // Update the local agents array if it exists
    const index = this.agents.findIndex((a) => a.uid === id);
    if (index > -1) {
      this.agents[index] = agent;
    }
    return agent;
  }

  async deleteAgent(agentId: string) {
    const index = this.agents.findIndex((agent) => agent.uid === agentId);
    if (index > -1) this.agents.splice(index, 1);
    await this.updateAgent(agentId, { deletedAt: new Date() });
    const channelsWithAgent = this.channelsService
      .chatbotChannels()
      .filter((channel) => channel.activeAgent === agentId);

    const promises = [];
    for (const channel of channelsWithAgent) {
      if (channel.uid) {
        promises.push(
          this.firestore.updateDoc('channel', channel.uid, {
            activeAgent: null,
          }),
        );
      }
    }
    if (promises.length > 0) {
      await Promise.all(promises);
    }
  }

  async updateAgent(id: string, agent: Partial<IAgent>) {
    const index = this.agents.findIndex((a) => a.uid === id);
    if (index > -1) {
      this.agents[index] = Object.assign(this.agents[index], agent);
    }
    await this.firestore.updateDoc('agent', id, agent);
    const currentAgent = this.agentSubject.value;
    if (currentAgent.uid === id) {
      this.setAgent({ ...currentAgent, ...agent });
    }
  }

  async createAgent(agent: Partial<IAgent>) {
    const newAgentDocRef = await this.firestore.addDoc('agent', {
      ...agent,
      organization: this.userService.organization()?.uid,
      createdAt: new Date(),
      deletedAt: null,
    });
    const newAgent = { ...agent, uid: newAgentDocRef.id } as IAgent;
    this.agents.unshift(newAgent);
    this.setAgent(newAgent);
    return newAgentDocRef;
  }

  setAgent(agent: IAgent) {
    this.agentSubject.next(agent);
  }

  getCurrentAgent(): IAgent {
    return this.agentSubject.value;
  }

  resetMessages() {
    this.messageContent = undefined;
    this.exampleMessages = [];
  }
}
